<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localeIsModalOpen"
    width="2xl"
    :title="localInvoiceItem?.name[$i18n.locale]"
    close-label-as-cancel
  >
    <template #body>
      <div
        class="-mr-3 -ml-1 flex flex-1 flex-col justify-between overflow-y-auto pr-3 pl-1"
      >
        <div v-if="invoiceItem" class="flex min-h-0 flex-1 flex-col gap-5">
          <div class="flex flex-col gap-2">
            <div class="flex-shrink-0">
              <BaseShowEditTranslation
                v-model:translations="localInvoiceItem.name"
                :edition-mode="editable"
                :label="$t('attributes.name')"
                :errors="errors?.name?.[$i18n.locale]"
              />
            </div>
            <div class="flex-shrink-0">
              <BaseShowEditTranslation
                v-model:translations="localInvoiceItem.description"
                :edition-mode="editable"
                :label="$t('attributes.description')"
                field-type="textarea"
                :errors="errors.description"
              />
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div class="grid flex-shrink-0 grid-cols-2 gap-3">
              <div class="col-span-1">
                <BaseShowEditInput
                  v-model="localInvoiceItem.quantity"
                  :edition-mode="editable"
                  :label="$t('attributes.quantity')"
                  :errors="errors.quantity"
                />
              </div>
              <div class="col-span-1">
                <BaseShowEditCheckBoxSimple
                  v-if="$can('unlock_invoicing_reduction')"
                  v-model="localInvoiceItem.lock_reduction"
                  :label="$t('global.lock_reduction')"
                  name="lock_reduction"
                  :edition-mode="editable"
                />
                <ReductionTypeComponent
                  v-model:reduction="localInvoiceItem.reduction"
                  v-model:type="localInvoiceItem.reduction_type"
                  :label="$t('attributes.reduction')"
                  name="reduction"
                  :errors="errors.reduction"
                  :edition-mode="editable && !localInvoiceItem.lock_reduction"
                />
              </div>
            </div>
            <div>
              <WalkmanButton
                v-model="localInvoiceItem.pricing_basis"
                :label="$t('attributes.pricing_basis')"
                size="extra-small"
                :options="[
                  {
                    value: 'HTVA',
                    label: $t('invoicing.excluding_vat_abbreviated')
                  },
                  {
                    value: 'TVAC',
                    label: $t('invoicing.including_vat_abbreviated')
                  }
                ]"
              />
            </div>
            <div v-if="!noPrice" class="flex-shrink-0">
              <BaseShowEditPricingInput
                v-model:htva-value="localInvoiceItem.price_htva"
                v-model:tvac-value="localInvoiceItem.price_tvac"
                v-model:tva-value="localInvoiceItem.price_tva_value"
                v-model:locked="localInvoiceItem.lock_price"
                :can-lock-unlock="$can('unlock_invoicing_price')"
                :label="$t('attributes.price')"
                :edition-mode="editable"
                :vat-rate="localVatRate"
                :pricing-basis="localInvoiceItem.pricing_basis"
                :currency="currency"
                :errors-price-htva="errors?.price_htva"
                :errors-price-tvac="errors?.price_tvac"
                :errors-price-tva-value="errors?.price_tva_value"
                is-vertical
                display-lock-button
              />
            </div>
            <div
              v-if="!noPrice && localInvoiceItem.item.advanced_pricing"
              class="grid grid-cols-3 gap-3"
            >
              <div class="col-start-2">
                <ItemPricingSelector
                  v-model:selected-pricing-id="localPricingId"
                  :item="localInvoiceItem.item"
                  :currency="invoice.currency"
                  @update:selected-pricing-id="handleItemPricingChange"
                />
              </div>
            </div>
          </div>
          <div class="col-span-1">
            <BaseShowEditCheckBoxSimple
              v-model="localInvoiceItem.hide_on_inline_invoice"
              :label="$t('attributes.hide_on_inline_invoice')"
              name="hide_on_inline_invoice"
              :edition-mode="editable"
            />
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex-shrink-0">
              <SelectInvoicingCategory
                v-model="localInvoiceItem.invoicing_category_id"
                :edition-mode="editable"
                :required="true"
              />
            </div>
          </div>
          <div
            v-if="
              [Country.FRANCE, Country.MARTINIQUE].includes(default_country)
            "
            class="flex flex-col gap-2"
          >
            <div class="flex flex-col gap-3 rounded bg-gray-200 p-2">
              <h3 class="font-bold leading-none">avant Juillet 2025</h3>
              <div class="flex flex-col gap-2">
                <SelectNormalisedCategoryDevis
                  v-model="localInvoiceItem.category_devis_normalised"
                  :edition-mode="editable"
                  :required="true"
                />
                <SelectNormalisedItemType
                  v-model="localInvoiceItem.item_type_devis_normalised"
                  :edition-mode="editable"
                  :required="true"
                />
              </div>
            </div>
            <div class="flex flex-col gap-3 rounded bg-gray-200 p-2">
              <h3 class="font-bold leading-none">après Juillet 2025</h3>
              <div class="flex flex-col gap-2">
                <SelectNormalisedCategoryDevis
                  v-model="localInvoiceItem.category_devis_normalised_2025"
                  :edition-mode="editable"
                  :required="true"
                  :old-list="false"
                />
                <SelectNormalisedItemType
                  v-model="localInvoiceItem.item_type_devis_normalised_2025"
                  :edition-mode="editable"
                  :required="true"
                  :old-list="false"
                />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex-shrink-0">
              <BaseShowEditTextArea
                v-model="localInvoiceItem.internal_note"
                :label="$t('attributes.internal_note')"
                :placeholder="$t('attributes.internal_note')"
                :edition-mode="true"
                name="internal_note"
              />
            </div>
            <div class="grid grid-cols-5 gap-2">
              <div class="col-span-3">
                <BaseCheckBox
                  v-model="localInvoiceItem.is_third_party_cost"
                  :label="$t('attributes.is_third_party_cost')"
                  :edition-mode="editable"
                  :disabled="
                    localInvoiceItem.is_third_party_cost &&
                    localInvoiceItem.third_party_cost_payment_id
                  "
                />
              </div>
              <div class="col-span-2 mt-2">
                <WalkmanButton
                  v-if="localInvoiceItem.is_third_party_cost"
                  v-model="localInvoiceItem.paid"
                  :options="[
                    { value: true, label: $t('global.paid') },
                    { value: false, label: $t('global.not_paid') }
                  ]"
                  :edition-mode="false"
                />
              </div>
              <div
                v-if="localInvoiceItem.third_party_cost_payment_id && isOnThirdPartyCostRoute"
                class="col-span-2 col-start-1 mt-2"
              >
                <BaseButton
                  :icon="'eye'"
                  color="warning"
                  :title="$t('global.see_settlement')"
                  @click="openEditFormPayment"
                />
              </div>
            </div>
            <div
              v-if="localInvoiceItem.is_third_party_cost"
              class="mt-3 flex-shrink-0"
            >
              <BaseShowEditTextArea
                v-model="localInvoiceItem.payment_note"
                :label="$t('attributes.payment_note')"
                :edition-mode="true"
                name="payment_note"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #cta>
      <div>
        <BaseButton
          :title="$t('button.send')"
          theme-color="bg-primary text-contrast-primary"
          icon="paper-plane"
          @click="submit"
        />
      </div>
    </template>
  </BaseSlideOverModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea'
import WalkmanButton from '@c/WalkmanButton'
import BaseCheckBox from '@c/addf-package/components/BaseCheckBox/BaseCheckBox.vue'
import BaseShowEditTranslation from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTranslation.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import BaseShowEditPricingInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditPricingInput.vue'
import SelectNormalisedCategoryDevis from '@c/InitialStateFormComponent/Enums/SelectNormalisedCategoryDevis.vue'
import SelectNormalisedItemType from '@c/InitialStateFormComponent/Enums/SelectNormalisedItemType.vue'
import SelectInvoicingCategory from '@c/InitialStateFormComponent/Enums/SelectInvoicingCategory.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import Status from '@/assets/enums/invoicing/Status'
import { Country } from '@/assets/enums'
import DocTypes from '@/assets/enums/invoicing/DocTypes'
import ReductionTypeComponent from '@/views/invoicing/show/tables/dialogs/components/ReductionTypeComponent.vue'
import BaseShowEditCheckBoxSimple from '@c/BaseShowEditCheckBoxSimple.vue'
import ItemPricingSelector from '@ui/modal/item/components/components/ItemPricingSelector.vue'

export default {
  name: 'InvoiceItemEditDialog',
  components: {
    ItemPricingSelector,
    BaseShowEditCheckBoxSimple,
    ReductionTypeComponent,
    BaseButton,
    SelectInvoicingCategory,
    SelectNormalisedItemType,
    SelectNormalisedCategoryDevis,
    BaseShowEditPricingInput,
    BaseShowEditInput,
    BaseShowEditTranslation,
    BaseCheckBox,
    WalkmanButton,
    BaseShowEditTextArea,
    BaseSlideOverModal
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    invoiceItem: {
      type: Object,
      required: true
    },
    isForInvoice: {
      type: Boolean,
      required: false,
      default: true
    },
    noPrice: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:isModalOpen', 'refreshList', 'open-payment'],
  data() {
    return {
      localInvoiceItem: this.$h.deepClone(this.invoiceItem),
      localVatRate: this.invoiceItem.vat_rate
    }
  },
  computed: {
    Country() {
      return Country
    },
    isOnThirdPartyCostRoute() {
      return ['third_party_cost_index_route', 'third_party_cost_index_route_id'].includes(
        this.$route.name
      )
    },
    ...mapGetters({
      stateInvoice: 'invoicing/getInvoice',
      initialMetadata: 'metadata/getInitialMetadata',
      errors: 'auth/getErrors',
      defaultCountry: 'metadata/getDefaultCountry',
      currencySettings: 'metadata/getDefaultCurrencyMetadata'
    }),
    default_country() {
      return this.invoice?.funeral_home?.address?.country ?? this.defaultCountry
    },
    invoice() {
      return this.invoiceItem.invoice ?? this.stateInvoice
    },
    /**
     * Mirrors the API guard in UpdateInvoiceItemController: outside of a draft
     * or a quote, the API keeps only internal_note, is_third_party_cost, paid,
     * payment_note, status and hide_on_inline_invoice. Any other field shown as
     * editable here would be silently dropped on save.
     */
    editable() {
      return (
        this.invoice.status === Status.DRAFT ||
        this.invoice.doc_type === DocTypes.QUOTE
      )
    },
    currency() {
      return this.invoice?.currency ?? this.currencySettings
    },
    categoryDevisNormalised() {
      return this.invoiceItem.category_devis_normalised
    },
    localeIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:isModalOpen', value)
      }
    },
    priceNotEncodingType() {
      const price = this.initialMetadata?.settings?.price_encoding_type
      return price === 'HTVA' ? 'tvac' : 'htva'
    },
    localPricingId() {
      return this.invoiceItem.item.prices.find(
        (p) => p.vat_rate === this.localVatRate
      )['id']
    }
  },
  methods: {
    ...mapActions({
      processUpdateSingleInvoiceItemAction: 'invoicing/updateSingleInvoiceItem'
    }),
    submit() {
      let message_success = this.$t('toasts.invoice_item_updated')
      if (this.localInvoiceItem.item.advanced_pricing) {
        this.localInvoiceItem.vat_rate = this.localVatRate
        this.localInvoiceItem.accounting_code =
          this.invoiceItem.item.prices.find(
            (p) => p.vat_rate === this.localVatRate
          ).accounting_code
      }
      this.processUpdateSingleInvoiceItemAction({
        id: this.invoiceItem.id,
        invoice_id: this.invoiceItem.invoice_id,
        invoice_item: {
          ...this.localInvoiceItem
        },
        for_invoice: this.isForInvoice
      }).then((_response) => {
        this.$h.toastSuccess(message_success)
        this.localeIsModalOpen = false
        this.$emit('refreshList')
      })
    },
    openEditFormPayment() {
      this.$emit('open-payment', this.invoiceItem.thirdPartyCostPayment)
    },
    handleItemPricingChange(value) {
      this.localVatRate = this.invoiceItem.item.prices.find(
        (p) => p.id === value
      ).vat_rate
    }
  }
}
</script>
